import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import type { IGetRowsParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';

import { catalogUEROtherCostTemplatesGql } from '../catalogUerOtherCostTemplatesQueries';

interface IUseCatalogOtherCostTemplates {
    filterValues: Record<string, string[]>;
    id: string | undefined;
}

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    return {
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (value.length) {
                acc = [
                    ...acc,
                    {
                        [key]: { in: value },
                    },
                ];
            }

            return acc;
        }, []),
    };
};

export const useCatalogOtherCostTemplatesData = ({ filterValues, id }: IUseCatalogOtherCostTemplates) => {
    const [catalogOtherCostTemplatesResponse, , , catalogOtherCostTemplatesMakeRequest] = useRequest({
        requestId: 'catalogOtherCostTemplates',
        query: catalogUEROtherCostTemplatesGql,
        withEffect: false,
    });

    const variables = useMemo(
        () =>
            id
                ? {
                      catalogUniqueId: id,
                      where: getWhereVariables(filterValues),
                  }
                : undefined,
        [filterValues, id],
    );

    const changeOrderHandler = useCallback((params: IGetRowsParams) => {
        const { sortModel } = params;

        const { sort, colId } = sortModel?.[0] ?? {};

        if (colId && sort) {
            return [{ [colId]: sort.toUpperCase() }];
        }

        return undefined;
    }, []);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'catalogOtherCostTemplates',
        requestFn: catalogOtherCostTemplatesMakeRequest,
        requestVariables: variables,
        customSortFn: changeOrderHandler,
    });

    useEffect(() => {
        if (catalogOtherCostTemplatesResponse?.catalogUEROtherCostTemplates?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                catalogOtherCostTemplatesResponse.catalogUEROtherCostTemplates?.items ?? [],
                catalogOtherCostTemplatesResponse.catalogUEROtherCostTemplates?.totalCount,
            );
        }
    }, [
        catalogOtherCostTemplatesResponse?.catalogUEROtherCostTemplates?.items,
        catalogOtherCostTemplatesResponse?.catalogUEROtherCostTemplates?.totalCount,
        filterValues,
        getRowParams,
    ]);

    const totalCount = useMemo(
        () => catalogOtherCostTemplatesResponse?.catalogUEROtherCostTemplates?.totalCount ?? 0,
        [catalogOtherCostTemplatesResponse?.catalogUEROtherCostTemplates?.totalCount],
    );

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams?.current?.endRow;
    }, [getRowParams, totalCount]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
            changeOrderHandler,
        }),
        [changeOrderHandler, dataSource, fetchedCount, totalCount],
    );
};
